import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useGradingCriteria } from "../../Query/useGradingCriteria";
import colors from "../../Constants/colors";
import { useKnowledgeList } from "../../Query/useKnowledge";
import { enqueueSnackbar } from "notistack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useCreateCertification,
  useSingleCertification,
  useUpdateCertification,
} from "../../Query/useCertifications";
import { getCertCategories, getQaTemplates } from "../../Api/certifications";
import { Certification, QaTemplate } from "../../Models/certification";
import { KnowledgeBaseStatus } from "../../Enums/companyStatus";
import ConfirmationDialog from "../KnowledgeBase/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

const CertCategory = () => {
  const { t } = useTranslation("certCategories");
  const { colors: workspace } = useWorkspaceColors();
  const [categoryName, setCategoryName] = useState("");
  const [isDigitalPerformance, setIsDigitalPerformance] = useState(false);
  const [knowledgeBaseIds, setKnowledgeBaseIds] = useState<number[]>([]);
  const [existingCategories, setExistingCategories] = useState<Certification[]>(
    [],
  );
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [openKnowledgeBaseSection, setOpenKnowledgeBaseSection] =
    useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{
    name: string;
    digital_performance: boolean;
    knowledge_base_ids: number[];
    alan_expert_id: string | null;
  } | null>(null);
  const [selectedExpertId, setSelectedExpertId] = useState<string | null>("");
  const navigate = useNavigate();
  const { certCategoryId } = useParams<{ certCategoryId?: string }>();
  const location = useLocation();
  const [templates, setTemplates] = useState<QaTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);

  const [selectedGradingCriterionId, setSelectedGradingCriterionId] = useState<
    number | null
  >(null);

  const {
    data: knowledgeBaseList,
    isLoading: knowledgeBaseListLoading,
    isError: knowledgeBaseListError,
  } = useKnowledgeList();

  const { mutate: createCategory, isPending: isCreating } =
    useCreateCertification();

  const {
    data: singleCertification,
    isLoading: isLoadingSingleCert,
    isError: isFetchingError,
    error: fetchingError,
  } = useSingleCertification(Number(certCategoryId));

  const { mutate: updateCategory, isPending: isUpdating } =
    useUpdateCertification();

  const { data: gradingCriteria, isLoading: isGradingLoading } =
    useGradingCriteria();

  useEffect(() => {
    if (certCategoryId) {
      setMode("edit");
    } else {
      setMode("create");
      setCategoryName("");
      setIsDigitalPerformance(false);
      setKnowledgeBaseIds([]);
      setSelectedExpertId("");
    }

    getQaTemplates().then((data) => {
      setTemplates(data);
    });

    getCertCategories().then((categories) => {
      setExistingCategories(categories);
    });
  }, [certCategoryId]);

  useEffect(() => {
    if (mode === "edit" && singleCertification) {
      setCategoryName(singleCertification.name);
      setIsDigitalPerformance(singleCertification.digital_performance);
      const kbIds = singleCertification.knowledge_bases.map((kb) => kb.id);
      setKnowledgeBaseIds(kbIds);
      setSelectedExpertId(singleCertification.expert?.alan_expert_id || "");
      setSelectedTemplateId(singleCertification.template?.id || 0);
      setSelectedGradingCriterionId(
        singleCertification.grading_criterion?.id || null,
      );
    }
  }, [mode, singleCertification]);

  useEffect(() => {
    if (isFetchingError && fetchingError.response?.status === 404) {
      enqueueSnackbar(
        "The requested Certification Category no longer exists.",
        { variant: "error" },
      );
      navigate("/cert_category", { replace: true });
    }
  }, [isFetchingError, fetchingError, navigate]);

  const closeDeleteDialog = () => {
    setIsDialogOpen(false);
    setPendingPayload(null);
  };

  const confirmCreateCategory = () => {
    if (pendingPayload) {
      createCategory(pendingPayload);
    }
    closeDeleteDialog();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (categoryName.trim() === "") {
      enqueueSnackbar("Category name cannot be empty.", { variant: "error" });
      return;
    }

    const categoryExists = existingCategories.some(
      (category) => category.name === categoryName,
    );

    const payload = {
      name: categoryName,
      digital_performance: isDigitalPerformance,
      knowledge_base_ids: knowledgeBaseIds,
      alan_expert_id: selectedExpertId || null,
      qa_insights_template_id: selectedTemplateId || 0,
      grading_criterion_id: selectedGradingCriterionId || null,
    };

    if (categoryExists && mode !== "edit") {
      setPendingPayload(payload);
      setIsDialogOpen(true);
    } else {
      if (mode === "create") {
        createCategory(payload);
      } else {
        updateCategory({
          id: Number(certCategoryId),
          updatedCategory: payload,
        });
      }
    }
  };

  const handleKnowledgeBaseToggle = (id: number) => {
    setKnowledgeBaseIds((prevIds) =>
      prevIds.includes(id)
        ? prevIds.filter((kbId) => kbId !== id)
        : [...prevIds, id],
    );
  };

  const sectionTitleSx = {
    color: workspace.primary_dark,
    fontWeight: 600,
    fontSize: "0.85rem",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${colors.gray200}`,
    pb: 0.5,
    mb: 2,
  };

  const lightSelectSx = {
    bgcolor: "white",
    fontSize: "0.875rem",
    borderRadius: 2,
    color: `${colors.primary_dark} !important`,
    "& .MuiSelect-select": { color: `${colors.primary_dark} !important` },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray300 },
  };

  const lightMenuProps = {
    PaperProps: {
      sx: {
        bgcolor: `${colors.white} !important`,
        border: `1px solid ${colors.gray200}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        "& .MuiMenuItem-root": {
          color: `${colors.primary_dark} !important`,
          fontSize: "0.875rem",
          "&:hover": { backgroundColor: `${colors.gray50} !important` },
          "&.Mui-selected": {
            backgroundColor: `${workspace.primary_dark}0F !important`,
            color: `${colors.primary_dark} !important`,
          },
          "&.Mui-selected:hover": {
            backgroundColor: `${workspace.primary_dark}1A !important`,
          },
        },
      },
    },
  };

  return (
    <>
      <Box
        sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
      >
        <Box
          sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              borderLeft: `4px solid ${workspace.primary_dark}`,
              background: colors.white,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton
                onClick={() => navigate("/cert_category")}
                size="small"
                sx={{ color: workspace.primary_dark }}
              >
                <ArrowBackIcon />
              </IconButton>
              <VerifiedOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: workspace.primary_dark }}
              >
                {mode === "create"
                  ? t("createCategoryTitle")
                  : t("editCategoryTitle")}
              </Typography>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              borderLeft: `4px solid ${workspace.primary_dark}`,
              background: colors.white,
            }}
          >
            {isLoadingSingleCert && !singleCertification ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                }}
              >
                <CircularProgress sx={{ color: workspace.accent }} />
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                key={location.pathname}
              >
                <Typography sx={sectionTitleSx}>{t("categoryName")}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ComoInput
                      label={t("categoryName")}
                      name="name"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      required
                      disabled={isCreating || isUpdating}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isDigitalPerformance}
                          onChange={(e) =>
                            setIsDigitalPerformance(e.target.checked)
                          }
                          sx={{
                            color: colors.gray400,
                            "&.Mui-checked": { color: workspace.accent },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: colors.primary_dark,
                            fontSize: "0.875rem",
                          }}
                        >
                          {t("digitalPerformance")}
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography sx={sectionTitleSx}>
                    {t("chooseQaInsights")}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={templates.length ? selectedTemplateId : ""}
                      onChange={(e) =>
                        setSelectedTemplateId(Number(e.target.value))
                      }
                      id="template-select"
                      disabled={templates.length === 0}
                      displayEmpty
                      sx={lightSelectSx}
                      MenuProps={lightMenuProps}
                    >
                      <MenuItem value={0} disabled>
                        <Typography
                          sx={{
                            color: colors.gray500,
                            fontSize: "0.875rem",
                            fontStyle: "italic",
                          }}
                        >
                          {t("chooseQaInsights")}
                        </Typography>
                      </MenuItem>
                      {templates.map((tpl) => (
                        <MenuItem key={tpl.id} value={tpl.id}>
                          {tpl.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography sx={sectionTitleSx}>
                    {t("attachKnowledgeBases")}
                  </Typography>
                  <Box
                    onClick={() => setOpenKnowledgeBaseSection((prev) => !prev)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      border: `1px solid ${colors.gray200}`,
                      borderRadius: 1.5,
                      backgroundColor: colors.gray50,
                      "&:hover": {
                        borderColor: workspace.primary_dark,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: colors.primary_dark,
                        fontSize: "0.875rem",
                        flex: 1,
                      }}
                    >
                      {knowledgeBaseIds.length} {t("selected")}
                    </Typography>
                    {openKnowledgeBaseSection ? (
                      <ExpandLessIcon sx={{ color: workspace.accent }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: workspace.accent }} />
                    )}
                  </Box>

                  <Collapse
                    in={openKnowledgeBaseSection}
                    timeout="auto"
                    unmountOnExit
                  >
                    {knowledgeBaseListLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: workspace.accent }}
                        />
                      </Box>
                    ) : knowledgeBaseListError ? (
                      <Typography
                        sx={{
                          color: colors.red700,
                          mt: 1,
                          fontSize: "0.875rem",
                        }}
                      >
                        {t("loading")}
                      </Typography>
                    ) : (
                      <FormControl
                        fullWidth
                        component="fieldset"
                        sx={{ mt: 1.5 }}
                      >
                        <FormGroup>
                          {knowledgeBaseList && knowledgeBaseList.length > 0 ? (
                            <Grid container spacing={1}>
                              {knowledgeBaseList.map((kb) => {
                                const disabled =
                                  kb.status !== KnowledgeBaseStatus.PROCESSED;
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={kb.id}>
                                    <Tooltip
                                      title={
                                        disabled
                                          ? t("knowledgeBaseNotAvailable")
                                          : ""
                                      }
                                      arrow
                                      disableHoverListener={!disabled}
                                    >
                                      <FormControlLabel
                                        value={kb.name}
                                        control={
                                          <Checkbox
                                            checked={knowledgeBaseIds.includes(
                                              kb.id,
                                            )}
                                            onChange={() =>
                                              handleKnowledgeBaseToggle(kb.id)
                                            }
                                            disabled={disabled}
                                            sx={{
                                              color: colors.gray400,
                                              "&.Mui-checked": {
                                                color: workspace.accent,
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography
                                            sx={{
                                              color: colors.primary_dark,
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {kb.name}
                                          </Typography>
                                        }
                                      />
                                    </Tooltip>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          ) : (
                            <Typography
                              sx={{
                                color: colors.gray500,
                                fontSize: "0.875rem",
                                fontStyle: "italic",
                              }}
                            >
                              {t("noKnowledgeBases")}
                            </Typography>
                          )}
                        </FormGroup>
                      </FormControl>
                    )}
                  </Collapse>
                </Box>
                <Box sx={{ mt: 4 }}>
                  <Typography sx={sectionTitleSx}>
                    {t("chooseGradingCriterion")}
                  </Typography>

                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedGradingCriterionId ?? ""}
                      onChange={(e) =>
                        setSelectedGradingCriterionId(
                          e.target.value === "" ? null : Number(e.target.value),
                        )
                      }
                      displayEmpty
                      disabled={
                        gradingCriteria?.length === 0 ||
                        isCreating ||
                        isUpdating
                      }
                      sx={lightSelectSx}
                      MenuProps={lightMenuProps}
                    >
                      <MenuItem value="">
                        <Typography
                          sx={{
                            color: colors.gray500,
                            fontSize: "0.875rem",
                            fontStyle: "italic",
                          }}
                        >
                          {t("chooseGradingCriterion")}
                        </Typography>
                      </MenuItem>

                      {gradingCriteria?.map((gc) => (
                        <MenuItem key={gc.id} value={gc.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <span>{gc.name}</span>
                            {gc.default && (
                              <VerifiedOutlinedIcon
                                sx={{ fontSize: 16, color: workspace.accent }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mt: 4 }}>
                  <ComoButton
                    type="submit"
                    disabled={isCreating || isUpdating}
                    fullWidth
                    sx={{ py: 1.25, fontWeight: 600 }}
                  >
                    {isCreating || isUpdating ? (
                      <CircularProgress size={20} />
                    ) : mode === "create" ? (
                      t("submit")
                    ) : (
                      t("update")
                    )}
                  </ComoButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <ConfirmationDialog
        open={isDialogOpen}
        onClose={closeDeleteDialog}
        title={t("duplicateCategoryTitle")}
        content={t("duplicateCategoryContent", { name: pendingPayload?.name })}
        actions={[
          { label: t("no"), onClick: closeDeleteDialog },
          { label: t("yes"), onClick: confirmCreateCategory },
        ]}
      />
    </>
  );
};

export default CertCategory;
